import { useEffect, useRef, useState } from "react";

import { api, ApiError, storeToken } from "../api";
import { Art } from "./Art";
import { PhoneInput } from "./PhoneInput";
import { t } from "../i18n";
import { isComplete, toE164 } from "../lib/phone";
import type { Locale, RecordMeta } from "../types";

/** Ile sekund czekamy z ponowną wysyłką: SMS bywa opóźniony o kilkadziesiąt sekund. */
const PRZERWA_S = 60;

interface Props {
  meta: RecordMeta;
  locale: Locale;
  onVerified: () => void;
}

/**
 * Bez logowania i haseł: magic link identyfikuje onboarding, a kod SMS potwierdza,
 * że po drugiej stronie jest właściciel numeru.
 */
export function Gate({ meta, locale, onVerified }: Props) {
  const [digits, setDigits] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [devCode, setDevCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Odliczanie do ponownej wysyłki. Kandydat, który nie doczekał się SMS-a,
  // klikał „Wyślij ponownie" co kilkanaście sekund i dostawał serię kodów
  // naraz (zgłoszenie 3328202790). Każdy z nich i tak działa, ale seria SMS-ów
  // myli, a limit kodów na godzinę szybko się kończy.
  const [czekaj, setCzekaj] = useState(0);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (sent) codeRef.current?.focus();
  }, [sent]);

  useEffect(() => {
    if (czekaj <= 0) return;
    const zegar = window.setTimeout(() => setCzekaj((s) => s - 1), 1000);
    return () => window.clearTimeout(zegar);
  }, [czekaj]);

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      // Numer znamy z zaproszenia. Puste pole nie jest deklaracją numeru —
      // wysyłamy null i backend użyje tego, do którego poszedł magic link.
      const response = await api.sendOtp(
        meta.record_id,
        isComplete(digits) ? toE164(digits) : null,
        locale,
      );
      setSent(true);
      setCzekaj(PRZERWA_S);
      setDevCode(response.dev_code);
    } catch (caught) {
      setError((caught as ApiError).message);
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    setError(null);
    try {
      const response = await api.verifyOtp(meta.record_id, code.trim());
      storeToken(meta.record_id, response.token);
      onVerified();
    } catch (caught) {
      setError((caught as ApiError).message);
    } finally {
      setBusy(false);
    }
  };

  const powitanie = meta.branding?.texts.welcome;

  return (
    <div className="gate gate--art">
      <Art name="start-link" size={168} className="gate__art" />

      {/* Powitanie marki to pierwsze zdanie, jakie kandydat od nas czyta —
          stoi nad numerem. Na szerokim ekranie to samo mówi kolumna z
          kontekstem, więc tam blok się chowa (CSS), a nagłówkiem karty
          zostaje sam numer telefonu. */}
      <div className="gate__welcome">
        <h1 className="gate__title">{powitanie?.title ?? t("asideTitle", locale)}</h1>
        <p className="gate__lead">{powitanie?.text ?? t("asideLead", locale)}</p>
      </div>

      <h2 className="gate__label">{t("otpTitle", locale)}</h2>

      <div className="gate__row">
        <PhoneInput
          digits={digits}
          onChange={setDigits}
          onEnter={() => !sent && send()}
          disabled={sent}
          label={t("otpTitle", locale)}
        />
        {/* Puls (samo krycie) mówi „wysyłamy", zanim wróci odpowiedź bramki. */}
        <button
          className={`btn btn--primary${busy ? " puls" : ""}`}
          onClick={send}
          disabled={busy || czekaj > 0}
        >
          {!sent ? t("send", locale) : czekaj > 0 ? t("resendIn", locale).replace("{s}", String(czekaj)) : t("resend", locale)}
        </button>
      </div>

      <p className="gate__hint">
        {sent ? t("otpSent", locale) : t("otpSubtitle", locale)}
        {!sent && meta.phone_hint && <> {meta.phone_hint}</>}
      </p>

      {sent && (
        // Wiersz kodu wjeżdża pod numer — bramka przechodzi z numeru do kodu
        // jednym ruchem zamiast skokowej podmiany.
        <div className="gate__row wjazd">
          <input
            ref={codeRef}
            className="input"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder={t("codePlaceholder", locale)}
            aria-label={t("codePlaceholder", locale)}
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
            onKeyDown={(event) => event.key === "Enter" && verify()}
          />
          <button
            className={`btn btn--primary${busy ? " puls" : ""}`}
            onClick={verify}
            disabled={busy || code.length < 6}
          >
            {t("verify", locale)}
          </button>
        </div>
      )}

      {error && <p className="error">{error}</p>}

      {devCode && (
        <p className="gate__dev wjazd">
          {t("devCode", locale)} <strong>{devCode}</strong>
        </p>
      )}
    </div>
  );
}
